import type { FieldErrors, UseFormRegister } from 'react-hook-form'
import { Cartao } from '../../../components/ListaDeDados'
import { CampoDeSelecao, CampoDeTexto, CampoDeTextoLongo } from '../../../components/campos'
import { OS } from '../textosDaOs'
import { FORMULARIO } from './textosDoFormulario'
import type { FormularioDeOs } from './esquema'

interface Props {
  register: UseFormRegister<FormularioDeOs>
  errors: FieldErrors<FormularioDeOs>
  /** O regime escolhido agora — decide o que faz sentido pedir. */
  regime: string
}

/**
 * As seções 1, 2 e 3 do modelo, mais as duas assinaturas.
 *
 * **Os campos que não existem no regime escolhido somem, em vez de ficarem desabilitados.** Mês só
 * na ordem mensal, fase Inicial/Final só na de projeto: são regras do contrato (a ordem do ANEXO IB
 * é uma por mês, 4.1.2.3.13, p.16; a do ANEXO IA atravessa meses, ANEXO V, p.110), e um campo
 * cinza pedindo para ser preenchido ensinaria o contrário do que o contrato diz.
 *
 * @param props o registrador e os erros do formulário, e o regime corrente
 * @returns as seções de identificação
 */
export function CamposDaIdentificacao({ register, errors, regime }: Props) {
  const mensal = regime === 'SUSTENTACAO_INFRAESTRUTURA'

  return (
    <>
      <Cartao titulo={FORMULARIO.secoes.documento}>
        <div className="grid gap-4 sm:grid-cols-2">
          <CampoDeSelecao rotulo={FORMULARIO.regime} erro={errors.regime?.message} {...register('regime')}>
            <option value="SUSTENTACAO_INFRAESTRUTURA">{OS.regimes.SUSTENTACAO_INFRAESTRUTURA}</option>
            <option value="PROJETO_PONTO_DE_FUNCAO">{OS.regimes.PROJETO_PONTO_DE_FUNCAO}</option>
          </CampoDeSelecao>

          <CampoDeTexto
            rotulo={FORMULARIO.numeroExterno}
            ajuda={OS.numeroExternoAjuda}
            erro={errors.numeroExterno?.message}
            {...register('numeroExterno')}
          />

          {mensal ? (
            <CampoDeTexto
              rotulo={FORMULARIO.competencia}
              ajuda={FORMULARIO.competenciaAjuda}
              type="month"
              erro={errors.competencia?.message}
              {...register('competencia')}
            />
          ) : (
            <>
              <CampoDeSelecao
                rotulo={FORMULARIO.tipo}
                ajuda={FORMULARIO.tipoAjuda}
                erro={errors.tipo?.message}
                {...register('tipo')}
              >
                <option value="">Escolha a fase</option>
                <option value="INICIAL">{OS.tipos.INICIAL}</option>
                <option value="FINAL">{OS.tipos.FINAL}</option>
              </CampoDeSelecao>
              <CampoDeTexto
                rotulo={FORMULARIO.ordemInicial}
                erro={errors.ordemDeServicoInicial?.message}
                {...register('ordemDeServicoInicial')}
              />
            </>
          )}

          <CampoDeTexto
            rotulo={FORMULARIO.periodoInicio}
            type="date"
            erro={errors.periodoInicio?.message}
            {...register('periodoInicio')}
          />
          <CampoDeTexto
            rotulo={FORMULARIO.periodoFim}
            type="date"
            erro={errors.periodoFim?.message}
            {...register('periodoFim')}
          />
          <CampoDeTexto
            rotulo={FORMULARIO.emitidaEm}
            type="date"
            erro={errors.emitidaEm?.message}
            {...register('emitidaEm')}
          />
        </div>
      </Cartao>

      <Cartao titulo={FORMULARIO.secoes.contratado}>
        <div className="space-y-4">
          <CampoDeTexto rotulo={FORMULARIO.contrato} erro={errors.contrato?.message} {...register('contrato')} />
          <CampoDeTextoLongo rotulo={FORMULARIO.objeto} rows={3} erro={errors.objeto?.message} {...register('objeto')} />
        </div>
      </Cartao>

      <Cartao titulo={FORMULARIO.secoes.pedido}>
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-3">
            <CampoDeTexto rotulo={FORMULARIO.unidade} erro={errors.unidade?.message} {...register('unidade')} />
            <CampoDeTexto
              rotulo={FORMULARIO.solicitanteNome}
              erro={errors.solicitanteNome?.message}
              {...register('solicitanteNome')}
            />
            <CampoDeTexto
              rotulo={FORMULARIO.solicitanteEmail}
              type="email"
              erro={errors.solicitanteEmail?.message}
              {...register('solicitanteEmail')}
            />
          </div>
          <CampoDeTextoLongo
            rotulo={FORMULARIO.objetivo}
            rows={3}
            erro={errors.objetivo?.message}
            {...register('objetivo')}
          />
          {!mensal && (
            <>
              <CampoDeTextoLongo
                rotulo={FORMULARIO.requisitosNaoFuncionais}
                rows={2}
                {...register('requisitosNaoFuncionais')}
              />
              <CampoDeTextoLongo rotulo={FORMULARIO.artefatosFornecidos} rows={2} {...register('artefatosFornecidos')} />
              <CampoDeTextoLongo rotulo={FORMULARIO.artefatosAGerar} rows={2} {...register('artefatosAGerar')} />
            </>
          )}
        </div>
      </Cartao>

      <Cartao titulo={FORMULARIO.secoes.assinaturas}>
        <div className="grid gap-4 sm:grid-cols-3">
          <CampoDeTexto
            rotulo={FORMULARIO.fiscalNome}
            erro={errors.fiscalRequisitanteNome?.message}
            {...register('fiscalRequisitanteNome')}
          />
          <CampoDeTexto
            rotulo={FORMULARIO.fiscalMatricula}
            erro={errors.fiscalRequisitanteMatricula?.message}
            {...register('fiscalRequisitanteMatricula')}
          />
          <CampoDeTexto
            rotulo={FORMULARIO.fiscalAssinouEm}
            type="date"
            erro={errors.fiscalRequisitanteAssinouEm?.message}
            {...register('fiscalRequisitanteAssinouEm')}
          />
          <CampoDeTexto
            rotulo={FORMULARIO.gestorNome}
            erro={errors.gestorNome?.message}
            {...register('gestorNome')}
          />
          <CampoDeTexto
            rotulo={FORMULARIO.gestorMatricula}
            erro={errors.gestorMatricula?.message}
            {...register('gestorMatricula')}
          />
          <CampoDeTexto
            rotulo={FORMULARIO.gestorAssinouEm}
            type="date"
            erro={errors.gestorAssinouEm?.message}
            {...register('gestorAssinouEm')}
          />
        </div>
      </Cartao>
    </>
  )
}
